import React, { useEffect, useContext, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { message } from "antd";
import { WarningFilled } from "@ant-design/icons";
import { AppContext } from "../../contexts/AppContext";
import Dialog from "../../components/Dialog/Dialog";
import GeneralButton from "../../components/GeneralButton/GeneralButton";
import Toast from "../../components/Toast/Toast";
import { createDateMessage, getConstValue } from "../../utils/commonUtils";
import "../../styles/globals.css";
import "./ReservationInformationPage.css";
import {
  getReservationMenus,
  getReservationDetail,
  cancelReservation,
} from "../../services/service";
import { DATETIME_SELECT_MODE, APPOINTMENT_TYPES } from "../../config/setting";

/**
 * 予約内容情報の確認画面用のコンポーネント
 * @param {} なし
 * @return {}  なし
 */
function ReservationInformationPage() {
  const {
    setGlobalMedicalInstitutionId,
    setGlobalMedicalInstitutionName,
    globalMedicalInstitutionName,
    globalReservationDetail,
    setGlobalReservationDetail,
    setGlobalDatetimeSelectMode,
    globalMedicalInstitutionId,
    setGlobalReservationToken,
    globalLoading,
  } = useContext(AppContext);
  const navigate = useNavigate();
  const location = useLocation();
  const [isDialogOpen, setDialogOpen] = useState(false);
  const [reservationToken, setReservationToken] = useState(null);
  const [questionnaireToken, setQuestionnaireToken] = useState(null);

  const [errorMessage, setErrorMessage] = useState(null);

  /**
   * コンポーネントがマウントされた時
   */
  useEffect(() => {
    // データ読込処理
    const loadDatas = async () => {
      try {
        const useQuery = new URLSearchParams(location.search);
        const token = useQuery.get("token") || null;
        setReservationToken(token);
        setGlobalReservationToken(token);

        const hash = window.location.hash;
        const params = new URLSearchParams(hash.substring(1)); // '#'を除去してから解析
        const medicalInstitutionId = params.get("medicalInstitutionId") || null;

        setGlobalMedicalInstitutionId(medicalInstitutionId);

        // 予約メニュー一覧情報取得
        const reservationMenus = await getReservationMenus(
          medicalInstitutionId
        );
        setGlobalMedicalInstitutionName(reservationMenus.name);

        // 予約メニュー一覧情報取得
        const reservationInformation = await getReservationDetail(token);
        setGlobalReservationDetail(reservationInformation?.data);
        setQuestionnaireToken(reservationInformation?.data?.questionnaireToken);
      } catch (error) {
        console.error("Error load data:", error);
      }
    };

    if (!globalLoading) {
      loadDatas();
    }
  }, [globalLoading]);

  /**
   * 問診入力ボタンクリック時
   * @param {} なし
   * @return {}  なし
   */
  const questionnaireEntryClick = () => {
    // 問診入力画面に遷移
    navigate(
      `/questionnaire-entry?token=${questionnaireToken}#medicalInstitutionId=${globalMedicalInstitutionId}`
    );
  };

  /**
   * 予約キャンセルボタンクリック時
   * @param {} なし
   * @return {}  なし
   */
  const cancelReservationClick = async () => {
    openDialog(true);
  };

  /**
   * 予約変更ボタンクリック時
   * @param {} なし
   * @return {}  なし
   */
  const reservationChangeClick = () => {
    setGlobalDatetimeSelectMode(DATETIME_SELECT_MODE.CHANGE);

    // 日時選択画面に遷移
    navigate("/datetime-select");
  };

  // トースト通知を表示する関数
  const showToast = (content, type) => {
    const key = `open${Date.now()}`;
    const closeToast = () => {
      message.destroy(key);
    };

    message.open({
      content: <Toast content={content} type="check" onClose={closeToast} />,
      key,
      className: `ant-message-notice-content-${type}`,
    });
  };

  /**
   * ダイアログのボタンがクリックされたときの処理
   * @param {number} buttonIndex - クリックされたボタンのインデックス
   */
  const handleButtonClick = async (buttonIndex) => {
    setDialogOpen(false);

    if (buttonIndex === 0) {
      // 予約キャンセル
      try {
        const data = await cancelReservation(reservationToken);

        if (data.result.status === "OK") {
          showToast("予約をキャンセルしました", "success");

          // 再取得
          const reservationInformation = await getReservationDetail(
            reservationToken
          );
          setGlobalReservationDetail(reservationInformation.data);
        } else {
          // エラーメッセージセット
          setErrorMessage(data?.result?.error_message);
        }
      } catch (error) {
        console.error("Error cancelReservation:", error);
        showToast("予約キャンセルに失敗しました", "error");
      }
    }
  };

  // ダイアログを開く
  const openDialog = () => setDialogOpen(true);

  // ダイアログを閉じる
  const closeDialog = () => setDialogOpen(false);

  const buttons = [
    {
      label: "予約をキャンセル",
      width: "120px",
      type: "primary-red",
      onClick: handleButtonClick,
    },
    {
      label: "戻る",
      width: "40px",
      type: "secondary-white",
      onClick: handleButtonClick,
    },
  ];

  return (
    <div className="patient-select">
      <div className="main-area">
        <div className="title">予約内容の確認</div>
        {errorMessage && (
          <div className="error-area">
            <div className="icon-area">
              <WarningFilled className="icon" />
            </div>
            <label>{errorMessage}</label>
          </div>
        )}
        <div className="detail">現在予約受付している詳細になります。</div>
        {globalReservationDetail ? (
          <div className="card-area">
            <div className="card">
              <div className="card-title">{globalMedicalInstitutionName}</div>
              <div className="card-item">
                <div className="card-item-name">予約日時</div>
                <div className="card-item-value">
                  {createDateMessage(globalReservationDetail?.reservationDate)}
                  <br />
                  {globalReservationDetail?.reservationTime}
                </div>
              </div>
              <div className="card-item">
                <div className="card-item-name">診療科</div>
                <div className="card-item-value">
                  {globalReservationDetail?.reservationMenuName}
                </div>
              </div>
              <div className="card-item">
                <div className="card-item-name">初再診</div>
                <div className="card-item-value">
                  {getConstValue(
                    APPOINTMENT_TYPES,
                    globalReservationDetail?.appointmentStatus,
                    "name"
                  )}
                </div>
              </div>
              <div className="card-item">
                <div className="card-item-name">受診者名</div>
                <div className="card-item-value">
                  {globalReservationDetail?.patientName}
                </div>
              </div>
              <div className="buttons-area">
                {globalReservationDetail?.questionnaireToken && (
                  <GeneralButton
                    buttonLabel={"問診入力"}
                    type={"primary-blue"}
                    onClick={() => questionnaireEntryClick()}
                  />
                )}
                <div className="row">
                  <div className="button-area">
                    <GeneralButton
                      buttonLabel={"予約キャンセル"}
                      type={"secondary-red"}
                      onClick={() => cancelReservationClick()}
                    />
                  </div>
                  <div className="button-area">
                    <GeneralButton
                      buttonLabel={"予約変更"}
                      type={"secondary-blue"}
                      onClick={() => reservationChangeClick()}
                    />
                  </div>
                </div>
              </div>
            </div>
            <Dialog
              title={"予約キャンセル"}
              isOpen={isDialogOpen}
              onClose={closeDialog}
              buttons={buttons}
            >
              <div className="dialog-bold-area">
                {createDateMessage(globalReservationDetail?.reservationDate)}
                <br />
                {globalReservationDetail?.time}
              </div>
              <div className="dialog-normal-area">
                {"以上の予約をキャンセルします。よろしいですか？"}
              </div>
            </Dialog>
          </div>
        ) : (
          // メニューなし
          <div className="none-data">現在、予約受付がありません</div>
        )}
      </div>
    </div>
  );
}

export default ReservationInformationPage;
